/* eslint complexity: ["error", { "max": 10 }] -- this is a utility file */
import { LDFlags } from "@/constants/FeatureFlags";
import { captureException, logInfo } from "@/services/AppMonitor";

const NetworkFailureSubstrings = ["networkfailure", "network failure"];
const IdentifyTimeoutSubstrings = ["identify timed out"];
const StreamingRelatedSubstrings = ["clientstream.launchdarkly.com", "/meval"];
const CanceledSubstrings = ["ioexception", "canceled", "unknown to request"];
const FeatureFlagFailureSubstrings = [
  "unknown feature flag",
  "returning default value",
];

const includesAnySubstring = ({
  message,
  substrings,
}: {
  message: string;
  substrings: string[];
}) => {
  const searchText = message.toLowerCase();
  return substrings.some((substring) => searchText.includes(substring));
};

const includesAllSubstrings = ({
  message,
  substrings,
}: {
  message: string;
  substrings: string[];
}) => {
  const searchText = message.toLowerCase();
  return substrings.every((substring) => searchText.includes(substring));
};

const isIdentifyTimeoutFailure = (message: string): boolean =>
  includesAnySubstring({ message, substrings: IdentifyTimeoutSubstrings });

const isNetworkFailure = (message: string): boolean => {
  // Suppress logging for known non-critical network errors as it was causing noise in the logs
  // https://teamequip.atlassian.net/browse/JN-1125
  // https://support.launchdarkly.com/hc/en-us/articles/12998125691419-Error-LaunchDarklyFlagFetchError-network-error
  const isGenericNetworkFailure = includesAnySubstring({
    message,
    substrings: NetworkFailureSubstrings,
  });
  // Suppress canceled/aborted LaunchDarkly streaming (SSE) connections
  // Example: java.io.IOException: Canceled, Unknown to request ... clientstream.launchdarkly.com/meval
  const isStreamingRelated = includesAnySubstring({
    message,
    substrings: StreamingRelatedSubstrings,
  });
  const isCanceled = includesAnySubstring({
    message,
    substrings: CanceledSubstrings,
  });
  const isStreamingCanceled = isStreamingRelated && isCanceled;

  return isGenericNetworkFailure || isStreamingCanceled;
};

const hasUnfamiliarFeatureFlag = (message: string): boolean => {
  // eg: 'unknown feature flag "disable-android-security-screen"; returning default value'
  const familiarFeatureFlags = Object.values(LDFlags).map(
    (flag) => `"${flag}"`,
  );

  // if any of the expected feature flags in this repo are in the error message, we suppress the error
  // as it is likely due to the known issue around client initialization failure spamming thrown errors

  // however if NONE of the expected feature flags in this repo are in the error message,
  // we do NOT suppress the error, as it is likely a different kind of error that we DO want to capture
  return includesAnySubstring({ message, substrings: familiarFeatureFlags });
};

const isFeatureFlagFailure = (message: string): boolean => {
  // most commonly happens when LaunchDarkly client fails to initialize,
  // meaning all feature flag accesses will fallback to their default values
  // we suppress this error as it means an error thrown for EACH feature flag, for EACH access
  // eg: 'unknown feature flag "disable-android-security-screen"; returning default value'
  const isCorrectKindOfError = includesAllSubstrings({
    message,
    substrings: FeatureFlagFailureSubstrings,
  });
  if (!isCorrectKindOfError) return false;

  return hasUnfamiliarFeatureFlag(message);
};

const handleLaunchDarklyError = (message: string) => {
  const normalizedMessage = message.toLowerCase();
  if (isIdentifyTimeoutFailure(normalizedMessage)) {
    logInfo({ message });
    return;
  }
  if (
    isNetworkFailure(normalizedMessage) ||
    isFeatureFlagFailure(normalizedMessage)
  ) {
    return;
  }
  captureException(new Error(message));
};

export { handleLaunchDarklyError };
